<template>
    <div>

        <div v-if="item.response">
            <DrawablePhoto
                v-for="(photo, index) in item.response"
                photoClass="mr-6 mb-3"
                :key="index"
                :image="photo.image"
                :drawing="photo.drawing"
                :thumbnail="photo.thumbnail"
                :width="photo.width"
                :height="photo.height"
                :loading="photo.loading"
                :allow-drawing="allowDrawing"
                @change="updateDrawing(index, arguments[0])"
                @delete="deletePhoto(index)"
            />
        </div>

        <div class="mt-2">
            <label v-if="canAddPhoto">
                <input
                    class="hidden"
                    type="file"
                    accept="image/*"
                    :disabled="loading"
                    :multiple="item.params.allow_multiple"
                    @change="addPhoto"
                />
                <span class="btn btn-default btn-primary main-button" :disabled="loading">
                    <Loader v-if="loading" />
                    <template v-else>
                        <svg class="mr-2" style="position: relative; top: -0.1rem;" xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 24 24" width="24" height="24">
                            <path
                                fill="var(--white)"
                                d="M20 7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9c0-1.1.9-2 2-2h2.38l1.73-3.45A1 1 0 0 1 9 3h6a1 1 0 0 1 .9.55L17.61 7H20zM9.62 5L7.89 8.45A1 1 0 0 1 7 9H4v10h16V9h-3a1 1 0 0 1-.9-.55L14.39 5H9.62zM12 17a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm0-2a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
                        </svg>
                        {{ addPhotoButtonTitle }}
                    </template>
                </span>
            </label>
        </div>
    </div>
</template>

<script>
    import Loader from '@/components/Icons/Loader.vue';
    import PhotoReader from '@/util/PhotoReader';
    import DrawablePhoto from '@/components/DrawablePhoto/DrawablePhoto.vue';
    import { eventBus } from '@/eventBus';

    const photoStubDictionary = [];

    export default {
        components: { DrawablePhoto, Loader },

        props: {
            item: {
                type: Object,
                required: true,
            },
        },

        data: () => ({
            loadingPhotos: 0,
        }),

        computed: {
            loading() {
                return this.loadingPhotos > 0;
            },

            addPhotoButtonTitle() {
                if (this.item.response && this.item.response > 0) {
                    return this.$t('Take another photo');
                }
                return this.$t('Take photo');
            },

            // when can a user upload a photo
            canAddPhoto() {
                if (this.item.params.allow_multiple) {
                    return true;
                }
                if (!this.item.response || this.item.response < 1) {
                    return true;
                }

                return false;
            },

            allowDrawing() {
                if (this.item.params.allow_drawing === true) {
                    return true;
                }

                if (this.item.params.allow_drawing === false) {
                    return false;
                }

                return true;
            },
        },

        methods: {

            deletePhoto(index) {
                this.item.response.splice(index, 1);
                this.$set(this.item, 'responded', this.item.response.length > 0);
            },

            updateDrawing(index, drawing) {
                this.item.response[index].drawing = drawing;
            },

            async addPhoto(event) {
                if (!event.target.files) return;

                if (!this.item.response) {
                    this.$set(this.item, 'response', []);
                }

                // first iteration creates the loading placeholders
                for (let i = 0; i < event.target.files.length; i++) {
                    const file = event.target.files[i];
                    const photoStub = {
                        loading: true,
                        image: null,
                        width: null,
                        height: null,
                        drawing: null,
                        thumbnail: null,
                    };
                    this.item.response.push(photoStub);
                    photoStubDictionary[i] = photoStub;
                    this.loadingPhotos += 1;
                }

                // second iteration loads and resizes the images, sequentially
                for (let i = 0; i < event.target.files.length; i++) {
                    const photoStub = photoStubDictionary[i];
                    if (!photoStub) continue;

                    try {
                        // eslint-disable-next-line no-await-in-loop
                        const {
                            photo, thumbnail, width, height,
                        } = await PhotoReader.read(event.target.files[i]);
                        photoStub.thumbnail = thumbnail;
                        photoStub.image = photo;
                        photoStub.width = width;
                        photoStub.height = height;
                        this.$delete(photoStub, 'loading');
                    } catch (error) {
                        const errorMessage = window.i18n.t('Error loading image: {error}', { error });
                        eventBus.$emit('error', errorMessage);
                        const index = this.item.response.indexOf(photoStub);
                        if (index !== -1) this.item.response.splice(index, 1);
                    }
                    this.loadingPhotos -= 1;
                    this.$set(this.item, 'responded', this.item.response.length > 0);
                }
                event.target.value = null;
            },
        },
    };
</script>
